import axios, { AxiosError, AxiosInstance } from 'axios';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Token refresh mutex to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

// token === null means the refresh failed; queued requests must then reject instead of hanging.
function onRefreshed(token: string | null) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // If 401 and we have a refresh token, try to refresh
    if (error.response?.status === 401 && originalRequest) {
      const { refreshToken, logout, setAuth, user: currentUser } = useAuthStore.getState();

      if (refreshToken && !originalRequest.url?.includes('/auth/refresh')) {
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshSubscribers.push((newToken) => {
              if (!newToken) {
                reject(error);
                return;
              }
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            });
          });
        }

        isRefreshing = true;
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken: newAccessToken, refreshToken: newRefreshToken, user } =
            response.data;

          // /auth/refresh returns only the token pair; keep the stored profile, otherwise
          // the user is cleared and ProtectedRoute bounces them to /login.
          const nextUser = user ?? currentUser;
          if (!nextUser) throw new Error('No user profile after token refresh');
          setAuth(nextUser, newAccessToken, newRefreshToken);
          isRefreshing = false;
          onRefreshed(newAccessToken);

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch {
          isRefreshing = false;
          onRefreshed(null);
          // Refresh failed, logout
          logout();
        }
      } else {
        logout();
      }
    }

    // Handle subscription-required errors (skip for subscription endpoints to avoid redirect loops)
    if (error.response?.status === 403 && !originalRequest?.url?.includes('/subscriptions/')) {
      const errorCode = (error.response?.data as Record<string, unknown>)?.error;
      const code = (errorCode as Record<string, unknown>)?.code;
      if (code === 'SUBSCRIPTION_REQUIRED' || code === 'SUBSCRIPTION_EXPIRED') {
        window.location.href = '/pharmacist/subscription';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export interface ApiError {
  code: string;
  message: string;
  errors?: Record<string, string[]>;
}

export function getApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    return error.response.data.error as ApiError;
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: error instanceof Error ? error.message : 'An unknown error occurred',
  };
}
